/**
 * Global per-rail collapse + resize state for ModulePageShell + SourcingRailedLayout.
 *
 * State (each persisted to localStorage):
 *   • leftCollapsed     ipclm:left-collapsed     '0' | '1'
 *   • rightCollapsed    ipclm:right-collapsed    '0' | '1'
 *   • leftWidth         ipclm:left-width         integer px (default 320)
 *   • rightWidth        ipclm:right-width        integer px (default 320)
 *
 * Triggers:
 *   • Click chevron button → toggle collapsed
 *   • Drag rail edge → resize width
 *   • Ctrl + [ → toggle left collapse
 *   • Ctrl + ] → toggle right collapse
 *   • Ctrl + \ → toggle both
 *
 * Widths are clamped to [180, 600] px when expanded, locked to 36 px when
 * collapsed. State broadcasts via window CustomEvent so all mounted
 * layouts stay in sync.
 */
import { useEffect, useState, useCallback } from 'react';

const KEYS = {
  L_COLLAPSED: 'ipclm:left-collapsed',
  R_COLLAPSED: 'ipclm:right-collapsed',
  L_WIDTH: 'ipclm:left-width',
  R_WIDTH: 'ipclm:right-width',
};
const EVENT = 'ipclm:rails-changed';

const DEFAULT_WIDTH = 320;
export const COLLAPSED_WIDTH = 36;
export const MIN_RAIL_WIDTH = 180;
export const MAX_RAIL_WIDTH = 600;

function readBool(key) {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeBool(key, v) {
  try { localStorage.setItem(key, v ? '1' : '0'); } catch { /* ignore */ }
}
function readInt(key, fallback) {
  try {
    const n = parseInt(localStorage.getItem(key) || '', 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch { return fallback; }
}
function writeInt(key, v) {
  try { localStorage.setItem(key, String(v)); } catch { /* ignore */ }
}

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

export function useRailsCollapsed() {
  const [leftCollapsed, setLeftCollapsed] = useState(() => readBool(KEYS.L_COLLAPSED));
  const [rightCollapsed, setRightCollapsed] = useState(() => readBool(KEYS.R_COLLAPSED));
  const [leftWidth, setLeftWidthState] = useState(() => readInt(KEYS.L_WIDTH, DEFAULT_WIDTH));
  const [rightWidth, setRightWidthState] = useState(() => readInt(KEYS.R_WIDTH, DEFAULT_WIDTH));

  // Cross-component sync
  useEffect(() => {
    const onChange = () => {
      setLeftCollapsed(readBool(KEYS.L_COLLAPSED));
      setRightCollapsed(readBool(KEYS.R_COLLAPSED));
      setLeftWidthState(readInt(KEYS.L_WIDTH, DEFAULT_WIDTH));
      setRightWidthState(readInt(KEYS.R_WIDTH, DEFAULT_WIDTH));
    };
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '[') {
        e.preventDefault();
        writeBool(KEYS.L_COLLAPSED, !readBool(KEYS.L_COLLAPSED));
        window.dispatchEvent(new Event(EVENT));
      } else if (e.key === ']') {
        e.preventDefault();
        writeBool(KEYS.R_COLLAPSED, !readBool(KEYS.R_COLLAPSED));
        window.dispatchEvent(new Event(EVENT));
      } else if (e.key === '\\') {
        e.preventDefault();
        const anyExpanded = !readBool(KEYS.L_COLLAPSED) || !readBool(KEYS.R_COLLAPSED);
        writeBool(KEYS.L_COLLAPSED, anyExpanded);
        writeBool(KEYS.R_COLLAPSED, anyExpanded);
        window.dispatchEvent(new Event(EVENT));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleLeft = useCallback(() => {
    writeBool(KEYS.L_COLLAPSED, !leftCollapsed);
    window.dispatchEvent(new Event(EVENT));
  }, [leftCollapsed]);

  const toggleRight = useCallback(() => {
    writeBool(KEYS.R_COLLAPSED, !rightCollapsed);
    window.dispatchEvent(new Event(EVENT));
  }, [rightCollapsed]);

  const setLeftWidth = useCallback((px) => {
    const clamped = clamp(Math.round(px), MIN_RAIL_WIDTH, MAX_RAIL_WIDTH);
    writeInt(KEYS.L_WIDTH, clamped);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const setRightWidth = useCallback((px) => {
    const clamped = clamp(Math.round(px), MIN_RAIL_WIDTH, MAX_RAIL_WIDTH);
    writeInt(KEYS.R_WIDTH, clamped);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return {
    leftCollapsed, rightCollapsed,
    toggleLeft, toggleRight,
    leftWidth, rightWidth,
    setLeftWidth, setRightWidth,
  };
}
